import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboard, listCampaigns } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ShoppingBag,
  TrendingUp,
  Banknote,
  Clapperboard,
  Wallet,
  Users,
  Ticket,
  Gift,
  Sparkles,
  Mail,
  MailOpen,
  PieChart,
  BarChart3,
  Loader2,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Rocket,
} from "lucide-react";

const RANGES = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "ytd", label: "YTD" },
  { key: "custom", label: "Custom" },
];

function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

function rangeToDates(range, custom) {
  const now = new Date();
  if (range === "day") {
    const d = isoDay(now);
    return { fromDate: d, toDate: d };
  }
  if (range === "week") {
    const from = new Date(now);
    from.setDate(from.getDate() - 6);
    return { fromDate: isoDay(from), toDate: isoDay(now) };
  }
  if (range === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { fromDate: isoDay(from), toDate: isoDay(now) };
  }
  if (range === "ytd") {
    const from = new Date(now.getFullYear(), 0, 1);
    return { fromDate: isoDay(from), toDate: isoDay(now) };
  }
  // custom
  return { fromDate: custom.from || "", toDate: custom.to || "" };
}

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function DealerDashboard({
  recId,
  companyName,
  logoUrl,
  personName,
  personEmail,
  roleLabel,
  avatarUrl,
  afterHeader = null,
  wrapReport = (node) => node,
  collapsibleReport = false,
  extraAction = null,
  reportExtra = null,
  extraKpi = null,
}) {
  const [extraOpen, setExtraOpen] = useState(false);
  const navigate = useNavigate();
  const [range, setRange] = useState("ytd");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(!collapsibleReport);

  const dates = useMemo(() => rangeToDates(range, custom), [range, custom]);

  const load = useCallback(async () => {
    if (!recId) return;
    if (range === "custom" && (!custom.from || !custom.to)) return;
    setLoading(true);
    try {
      const res = await fetchDashboard(recId, dates);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [recId, range, custom, dates]);

  useEffect(() => {
    load();
  }, [load]);

  const isAdmin = data?.is_admin;

  const report = (
        <div data-testid="dashboard-report">
        {loading ? (
          <div className="py-10 flex flex-col items-center text-zinc-400">
            <Loader2 className="animate-spin mb-2" size={20} />
            <span className="text-xs">Crunching numbers…</span>
          </div>
        ) : !data ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            Couldn&apos;t load your dashboard right now.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="dashboard-cards">
            <KpiCard
              icon={ShoppingBag}
              tone="violet"
              label={isAdmin ? "Sales (all)" : "Sales"}
              value={money(data.sales?.revenue ?? data.orders.revenue)}
              sub={`${(data.sales?.count ?? data.orders.count).toLocaleString()} sales · ${data.orders.count} web · ${data.invoices?.count ?? 0} invoice${(data.invoices?.count ?? 0) === 1 ? "" : "s"}`}
              onClick={() => navigate(`/app/sales-report?from=${dates.fromDate || ""}&to=${dates.toDate || ""}`)}
              testId="kpi-orders"
            />
            {data.videos && (
              <KpiCard
                icon={Clapperboard}
                tone="violet"
                label="Videos created"
                value={`${(data.videos.created || 0).toLocaleString()} video${data.videos.created === 1 ? "" : "s"}`}
                sub={`${data.videos.emails_sent || 0} email${data.videos.emails_sent === 1 ? "" : "s"} sent · ${data.videos.emails_opened || 0} opened · tap for details`}
                onClick={() => navigate("/app/videos-report")}
                testId="kpi-videos"
              />
            )}
            <KpiCard
              icon={TrendingUp}
              tone="emerald"
              label="Commission earned"
              value={money(data.commissions.earned)}
              sub={isAdmin ? "— dealers earn" : "in range"}
              onClick={() => navigate("/app/earnings")}
              testId="kpi-comm-earned"
            />
            <KpiCard
              icon={Banknote}
              tone="blue"
              label={isAdmin ? "Paid to dealers" : "Commission paid"}
              value={money(data.commissions.paid)}
              sub="in range"
              onClick={() => navigate("/app/earnings")}
              testId="kpi-comm-paid"
            />
            <KpiCard
              icon={Wallet}
              tone="amber"
              label="Commission balance"
              value={money(data.commissions.balance)}
              sub="current"
              onClick={() => navigate("/app/earnings")}
              testId="kpi-comm-balance"
            />
            <KpiCard
              icon={Users}
              tone="indigo"
              label={isAdmin ? "CRM" : "Contacts"}
              value={data.team.contacts != null ? data.team.contacts.toLocaleString() : "View"}
              sub={isAdmin ? "open directory" : "in your team"}
              onClick={() => navigate("/app/contacts")}
              testId="kpi-contacts"
            />
            <KpiCard
              icon={Ticket}
              tone="rose"
              label="Coupons created"
              value={data.coupons.created.toLocaleString()}
              sub="lifetime"
              onClick={() => navigate("/app/coupons")}
              testId="kpi-coupons"
            />
            <KpiCard
              icon={Gift}
              tone="fuchsia"
              label="Rewards"
              value={money(data.rewards.loyalty_points)}
              sub={
                <span className="inline-flex items-center gap-1">
                  <Sparkles size={11} className="text-violet-500" />
                  {data.rewards.ai_points} AI pts
                </span>
              }
              onClick={() => navigate("/app/earnings")}
              testId="kpi-rewards"
            />
            <KpiCard
              icon={PieChart}
              tone="cyan"
              label="Shares earned"
              value={Number(data.shares || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              sub="lifetime"
              onClick={() => navigate("/app/equity")}
              testId="kpi-shares"
            />
            <KpiCard
              icon={BarChart3}
              tone="lime"
              label="Volume on orders"
              value={money(data.volume)}
              sub="lifetime spend"
              onClick={() => navigate("/app/earnings")}
              testId="kpi-volume"
            />
            <KpiCard
              icon={data.emails.opened > 0 ? MailOpen : Mail}
              tone="teal"
              label="Email campaigns"
              value={`${data.emails.sent.toLocaleString()} sent`}
              sub={
                data.emails.sent > 0
                  ? `${data.emails.opened} opened · ${data.emails.open_rate}%`
                  : "no sends yet"
              }
              onClick={() => setCampaignsOpen(true)}
              testId="kpi-emails"
            />
            {extraKpi && (
              <KpiCard
                icon={extraKpi.icon || Rocket}
                tone="violet"
                label={extraKpi.label}
                value={extraKpi.value}
                sub={extraKpi.sub}
                onClick={() => setExtraOpen((o) => !o)}
                testId="kpi-onboarding"
              />
            )}
          </div>
        )}
        {extraOpen && reportExtra}
        </div>
  );

  return (
    <div className="mb-2" data-testid="admin-dashboard">
      {/* Identity + company header (merged) */}
      <Card className="rounded-2xl border-zinc-200 overflow-hidden mb-4">
        <div className="relative bg-gradient-to-br from-violet-700 via-violet-600 to-indigo-600 px-5 py-5">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5) 0, transparent 45%)",
            }}
          />
          <div className="relative flex items-center gap-4">
            {/* Person avatar */}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={personName || "Profile"}
                className="h-14 w-14 rounded-full object-cover ring-2 ring-white/70 shadow-md shrink-0"
                data-testid="dashboard-avatar"
              />
            ) : (
              <div className="h-14 w-14 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-white font-bold text-lg shrink-0">
                {(personName || "?").slice(0, 1).toUpperCase()}
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="text-base font-bold text-white truncate" data-testid="dashboard-person">
                {personName || "Your profile"}
              </div>
              {personEmail ? (
                <div className="text-xs text-white/70 truncate">{personEmail}</div>
              ) : null}
              {roleLabel ? (
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-white/20 px-2 py-0.5 rounded-full">
                  <ShieldCheck size={11} />
                  {roleLabel.toUpperCase()}
                </div>
              ) : null}
            </div>

            {/* Company logo */}
            {logoUrl ? (
              <div className="h-12 w-12 rounded-xl bg-white/95 p-1.5 shadow-md shrink-0 flex items-center justify-center">
                <img
                  src={logoUrl}
                  alt={companyName || "Logo"}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                  data-testid="dashboard-logo"
                />
              </div>
            ) : null}
          </div>

          {/* Company / dashboard line */}
          <div className="relative mt-3 flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">
              Dashboard
            </span>
            <span className="text-white/40">·</span>
            <span className="text-sm font-semibold text-white truncate" data-testid="dashboard-company">
              {companyName || "Your business"}
            </span>
          </div>
        </div>

        {/* Date range selector */}
        <div className="px-4 py-3 border-t border-zinc-100">
          <div className="flex gap-1 bg-zinc-100 rounded-xl p-1" data-testid="dashboard-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  range === r.key
                    ? "bg-white shadow-sm text-violet-700"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
                data-testid={`dashboard-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {collapsibleReport && (
            <button
              type="button"
              onClick={() => setReportOpen((o) => !o)}
              className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 py-1.5 rounded-lg hover:bg-violet-50 transition-colors"
              data-testid="dashboard-report-toggle"
            >
              <BarChart3 size={13} /> {reportOpen ? "Hide report" : "Show report"}
              <ChevronDown size={14} className={`transition-transform ${reportOpen ? "rotate-180" : ""}`} />
            </button>
          )}
          {extraAction}
          {range === "custom" && (
            <div className="flex items-center gap-2 mt-2">
              <Input
                type="date"
                value={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                className="h-9 rounded-lg text-xs"
                data-testid="dashboard-custom-from"
              />
              <span className="text-xs text-zinc-400">to</span>
              <Input
                type="date"
                value={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                className="h-9 rounded-lg text-xs"
                data-testid="dashboard-custom-to"
              />
            </div>
          )}
        </div>
        {collapsibleReport && reportOpen && (
          <div className="px-4 pb-4 pt-3 border-t border-zinc-100 bg-zinc-50/60" data-testid="dashboard-report-inline">
            {report}
          </div>
        )}
      </Card>

      {!collapsibleReport && wrapReport(report)}

      {afterHeader}


      <CampaignsDialog
        open={campaignsOpen}
        onOpenChange={setCampaignsOpen}
        recId={recId}
      />
    </div>
  );
}

const TONES = {
  violet: "bg-violet-50 text-violet-600",
  emerald: "bg-emerald-50 text-emerald-600",
  blue: "bg-blue-50 text-blue-600",
  amber: "bg-amber-50 text-amber-600",
  indigo: "bg-indigo-50 text-indigo-600",
  rose: "bg-rose-50 text-rose-600",
  fuchsia: "bg-fuchsia-50 text-fuchsia-600",
  teal: "bg-teal-50 text-teal-600",
  cyan: "bg-cyan-50 text-cyan-600",
  lime: "bg-lime-50 text-lime-600",
};

function KpiCard({ icon: Icon, tone, label, value, sub, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-2xl border border-zinc-200 bg-white p-3.5 transition-all hover:border-violet-300 hover:shadow-md active:scale-[0.98]"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${TONES[tone]}`}>
          <Icon size={17} />
        </div>
        <ChevronRight
          size={16}
          className="text-zinc-300 group-hover:text-violet-400 transition-colors"
        />
      </div>
      <div className="mt-2.5 text-[11px] uppercase tracking-wide text-zinc-400 font-semibold">
        {label}
      </div>
      <div className="text-lg font-bold text-zinc-900 leading-tight truncate">
        {value}
      </div>
      <div className="text-[11px] text-zinc-500 truncate">{sub}</div>
    </button>
  );
}

function CampaignsDialog({ open, onOpenChange, recId }) {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!open || !recId) return;
    setLoading(true);
    listCampaigns(recId)
      .then((res) => setCampaigns(res.campaigns || []))
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, [open, recId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto"
        data-testid="campaigns-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail size={18} className="text-violet-600" />
            Email campaigns
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Your throttled email campaigns and their progress.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center text-zinc-400">
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="py-8 text-center text-sm text-zinc-500" data-testid="campaigns-empty">
            No campaigns yet. Send a bulk email from the CRM to start one.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="campaigns-list">
            {campaigns.map((c) => {
              const pct = c.total ? Math.round((c.sent_count / c.total) * 100) : 0;
              return (
                <li
                  key={c.id}
                  className="rounded-xl border border-zinc-200 p-3"
                  data-testid={`campaign-row-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">
                        {c.subject || "(no subject)"}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {c.template_name} ·{" "}
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full capitalize ${
                        c.status === "completed"
                          ? "bg-emerald-100 text-emerald-700"
                          : c.status === "running"
                          ? "bg-blue-100 text-blue-700"
                          : c.status === "paused"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-zinc-100 text-zinc-600"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                    <div
                      className="h-full bg-violet-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-1 text-[11px] text-zinc-500">
                    {c.sent_count}/{c.total} sent
                    {c.failed_count > 0 ? ` · ${c.failed_count} failed` : ""}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
